import Immutable from 'immutable';
import * as React from 'react';

import { slugify } from '../../../shared/helpers';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';

import './TableOfContents.scss';

export interface Heading {
  type: 'HEADING';
  id: string;
  level: string;
  value: string;
}

interface HeadingNode {
  id: string;
  level: number;
  value: string;
  childrens: HeadingNode[];
  __parent?: HeadingNode;
}

interface TableOfContentsProps {
  headings: Immutable.List<Immutable.Record<Heading>>;
  makeLinkClickHandler: (id: string) => (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

interface TableOfContentsTreeProps {
  node: HeadingNode;
  makeLinkClickHandler: (id: string) => (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

const buildHeadingTree = (headings: Immutable.List<Immutable.Record<Heading>>): HeadingNode => {
  const root: HeadingNode = {
    id: '',
    level: 0,
    value: '',
    childrens: [],
  };

  let current = root;
  headings.forEach(heading => {
    const value = heading.get('value');
    const id = slugify(value);

    const level = parseInt(heading.get('level'), 10);
    while (current.level >= level && current.__parent) {
      current = current.__parent;
    }

    const newHeadingNode = {
      id,
      level,
      value,
      childrens: [],
      __parent: current,
    };
    current.childrens.push(newHeadingNode);
    current = newHeadingNode;
  });

  return root;
};

const TableOfContentsTree = (props: TableOfContentsTreeProps) => (
  <ul>
    {props.node.childrens.map(children => (
      <li key={children.id}>
        <a href={`#${children.id}`} onClick={props.makeLinkClickHandler(children.id)}>
          {children.value}
        </a>
        {children.childrens.length > 0 && <TableOfContentsTree node={children} makeLinkClickHandler={props.makeLinkClickHandler} />}
      </li>
    ))}
  </ul>
);

export const TableOfContents = ({ headings, makeLinkClickHandler }: TableOfContentsProps) => {
  const root = React.useMemo(() => buildHeadingTree(headings), [headings]);

  if (root.childrens.length === 0) {
    return null;
  }

  return (
    <div className="learn__content-index">
      <h2 className="learn__content-index--title">
        <FormattedMessageHelper id="learn.tableOfContents" />
      </h2>
      <TableOfContentsTree node={root} makeLinkClickHandler={makeLinkClickHandler} />
    </div>
  );
};
